import React, { createContext, useContext, useState, ReactNode, FC, useMemo } from 'react';
import Dialog, { DialogProps } from 'components/modals/Dialog';

type ShowDialogParams = Omit<DialogProps, 'open' | 'isLoading' | 'onCancel'> & {
  onCancel?: () => void;
};

interface DialogContextProps {
  showDialog: (props: ShowDialogParams) => void;
  closeDialog: () => void;
}

const DialogContext = createContext<DialogContextProps | undefined>(undefined);

const DialogProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [dialogProps, setDialogProps] = useState<ShowDialogParams | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const showDialog = (props: ShowDialogParams) => {
    setDialogProps(props);
  };

  const closeDialog = () => {
    setDialogProps(null);
  };

  const handleConfirm = async () => {
    if (dialogProps?.onConfirm) {
      setLoading(true);
      try {
        await dialogProps.onConfirm();
      } finally {
        setLoading(false);
      }
    }
  };

  const contextValue = useMemo(() => ({ showDialog, closeDialog }), [showDialog, closeDialog]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
      {dialogProps && (
        <Dialog
          isLoading={loading}
          title={dialogProps.title}
          content={dialogProps.content}
          confirmText={dialogProps.confirmText}
          cancelText={dialogProps.cancelText}
          onConfirm={handleConfirm}
          open={Boolean(dialogProps)}
          onCancel={() => {
            dialogProps.onCancel?.();
            closeDialog();
          }}
        />
      )}
    </DialogContext.Provider>
  );
};

const useDialog = (): DialogContextProps => {
  const context = useContext(DialogContext);
  if (context === undefined) {
    throw new Error('useDialog must be used within a DialogProvider');
  }
  return context;
};

export { DialogProvider, useDialog };
